/**
 * ResearchLab · /research · /deep-ai · /creativity
 *
 * Architect Directive Feb-2026 · Deep AI Research Lab.
 * DeepAI.org sets x-frame-options: ALLOWALL — fully embeddable.
 * Three tabs: Chat · Image Generator · Image Editor.
 * Iframes are conditionally rendered (lazy) — only the active tab's
 * iframe is in the DOM, so opening this page does not trigger 3 cold
 * cross-origin loads.
 */
import React, { useState } from "react";

const TABS = [
  {
    id:    "chat",
    label: "AI Chat",
    icon:  "✦",
    src:   "https://deepai.org/chat",
    title: "Deep AI Chat — Federation Research",
    blurb: "Conversational research console. Ask anything, get a baseline draft, then run the answer through the Federation Truth Gate before publishing.",
  },
  {
    id:    "image-gen",
    label: "Image Generator",
    icon:  "◈",
    src:   "https://deepai.org/machine-learning-model/text2img",
    title: "Deep AI Image Generator",
    blurb: "Text → image. For Federation press visuals, doctrine illustrations, and creative briefs. Verify provenance before any image enters the permanent record.",
  },
  {
    id:    "image-edit",
    label: "Image Editor",
    icon:  "▣",
    src:   "https://deepai.org/machine-learning-model/image-editor",
    title: "Deep AI Image Editor",
    blurb: "Inpaint, outpaint, upscale. Refine existing visuals without leaving the Cathedral.",
  },
];

export default function ResearchLab() {
  const [activeId, setActiveId] = useState(TABS[0].id);
  const active = TABS.find((t) => t.id === activeId) || TABS[0];

  return (
    <div
      data-testid="research-lab-page"
      className="px-6 lg:px-10 py-10 max-w-[1280px] mx-auto"
      style={{ color: "#cfeaf2", contentVisibility: "auto", containIntrinsicSize: "1200px 900px" }}
    >
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="mb-8">
        <div
          className="font-mono text-[10px] uppercase tracking-[0.34em] mb-3"
          style={{ color: "#ffbd00", textShadow: "0 0 10px rgba(255,189,0,0.55)" }}
        >
          ◆ Federation Tools · Research Lab
        </div>
        <h1
          className="font-display leading-none"
          style={{
            fontSize: "clamp(34px, 5.4vw, 60px)",
            color: "#fff7e0",
            textShadow: "0 0 22px rgba(255,189,0,0.45)",
          }}
        >
          Deep AI Research Lab
        </h1>
        <p
          className="mt-4 max-w-3xl"
          style={{ fontSize: 14, lineHeight: 1.7, color: "rgba(207,234,242,0.78)" }}
        >
          Powered by DeepAI.org · Integrated into the NextXus Federation. Use these tools
          for drafts, image scaffolding, and rapid creative iteration. Anything that lands
          in the permanent record still passes the 95% Truth Gate and the Ring of 12.
        </p>
      </header>

      {/* ── Tab bar ──────────────────────────────────────────── */}
      <div
        role="tablist"
        aria-label="Research Lab tools"
        data-testid="research-tablist"
        className="flex flex-wrap gap-2 mb-4"
      >
        {TABS.map((t) => {
          const isActive = t.id === activeId;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`research-panel-${t.id}`}
              data-testid={`research-tab-${t.id}`}
              onClick={() => setActiveId(t.id)}
              className="font-mono uppercase tracking-[0.22em] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                fontSize:   "11px",
                padding:    "10px 18px",
                color:      isActive ? "#0a0700" : "#ffd24a",
                background: isActive
                  ? "linear-gradient(135deg, #ffb200 0%, #ffd24a 100%)"
                  : "rgba(4,5,10,0.7)",
                border:     `1px solid ${isActive ? "#ffb200" : "rgba(255,189,0,0.40)"}`,
                boxShadow:  isActive
                  ? "0 0 18px rgba(255,189,0,0.55)"
                  : "0 0 0 rgba(0,0,0,0)",
                fontWeight: 700,
                cursor:     "pointer",
                willChange: "transform",
              }}
            >
              <span className="mr-2" aria-hidden="true">{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      <p
        className="text-[12px] mb-4"
        data-testid={`research-blurb-${active.id}`}
        style={{ color: "rgba(207,234,242,0.65)" }}
      >
        {active.blurb}
      </p>

      {/* ── Active iframe panel (lazy-rendered) ─────────────── */}
      <section
        role="tabpanel"
        id={`research-panel-${active.id}`}
        aria-labelledby={`research-tab-${active.id}`}
        data-testid={`research-panel-${active.id}`}
        className="w-full"
        style={{
          background:     "rgba(15, 23, 42, 0.85)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          border:         "1px solid rgba(255,189,0,0.30)",
          boxShadow:      "0 0 28px rgba(255,189,0,0.08)",
          borderRadius:   12,
          overflow:       "hidden",
        }}
      >
        <iframe
          key={active.id}
          src={active.src}
          title={active.title}
          data-testid={`research-iframe-${active.id}`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allow="clipboard-write"
          style={{
            width:        "100%",
            height:       "700px",
            border:       "none",
            borderRadius: 12,
            background:   "#0a0a14",
            display:      "block",
          }}
        />
      </section>

      {/* ── Operating doctrine ───────────────────────────────── */}
      <div
        className="mt-8 p-5 font-mono text-[11px] leading-relaxed"
        style={{
          background: "rgba(4,5,10,0.55)",
          border:     "1px solid rgba(255,189,0,0.22)",
          color:      "rgba(207,234,242,0.72)",
        }}
      >
        <div className="uppercase tracking-[0.28em] mb-2" style={{ color: "#ffbd00" }}>
          ◆ Use policy · DIR-CORE-001
        </div>
        Anything generated in this Lab is <span style={{ color: "#ffd24a" }}>scratch material</span>.
        It does not enter the Federation permanent record. To publish output here as
        Cathedral-sealed work, route the artifact through Core via
        <code style={{ color: "#7fe7f5" }}> POST /api/wire/library/publish</code> — Truth Gate
        verifies, Ed25519 signature seals, KEYS replicates.
      </div>
    </div>
  );
}
